import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AdminListToolbar from '../components/AdminListToolbar';
import AdminModal from '../components/AdminModal';
import { db, auth } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { storage } from '../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatTimestamp, toDateTimeLocal, timestampToDate } from '../utils/dateUtils';
import { runScheduleCheck, getScheduleIntervalMs } from '../utils/scheduleCheck';
import { pickAssignedChallengeIds, nextChallengeNumber } from '../utils/challengeOrder';
import { bundledLanguages } from '../translations';
import { getDefaultBranding, needsBrandingSeed, resolveBranding } from '../utils/platformDefaults';
import { normalizeHex, themeFromColors, validateThemeColors, THEME_PRESETS } from '../utils/platformTheme';
import { adminAccountErrorKey, callManageAdmin, changeOwnPassword } from '../utils/adminAccounts';
import './AdminPage.css';

// Fallback admin UUID constant
const FALLBACK_ADMIN_UID = 'sZQ7z2w0IIf14AQAEQgKqgaqrB32';
const RTL_LANGUAGES = ['ar', 'fa', 'prs', 'ur'];
const VALID_ADMIN_TABS = ['competitions', 'users', 'challenges', 'rounds', 'settings'];
const ADMIN_TAB_STORAGE_KEY = 'ezkAdminTab';
const BUNDLED_LANGUAGE_CODES = new Set(bundledLanguages.map((lang) => lang.shortcut));

const rememberAdminTab = (tab) => {
  if (!VALID_ADMIN_TABS.includes(tab)) return;
  try {
    sessionStorage.setItem(ADMIN_TAB_STORAGE_KEY, tab);
  } catch (err) {
    console.error('Error remembering admin tab:', err);
  }
};

const readRememberedAdminTab = () => {
  try {
    const saved = sessionStorage.getItem(ADMIN_TAB_STORAGE_KEY);
    return VALID_ADMIN_TABS.includes(saved) ? saved : 'competitions';
  } catch (err) {
    return 'competitions';
  }
};

const getChallengeSearchText = (challenge) => {
  const text = challenge.text || challenge.question;
  const parts = [challenge.id, challenge.category, challenge.type, challenge.number];
  if (typeof text === 'object' && text) {
    parts.push(...Object.values(text));
  } else if (text) {
    parts.push(String(text));
  }
  return parts.filter((part) => part !== undefined && part !== null).join(' ').toLowerCase();
};

const AdminPage = () => {
  const { t, availableLanguages, setAvailableLanguages, currentLanguage } = useLanguage();
  const isRtl = RTL_LANGUAGES.includes(currentLanguage);
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const location = useLocation();
  const activeTab = VALID_ADMIN_TABS.includes(tabParam) ? tabParam : 'competitions';
  const [competitions, setCompetitions] = useState([]);
  const [activeCompetition, setActiveCompetition] = useState(null);
  const [users, setUsers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ageSettings, setAgeSettings] = useState({ minAge: 0, maxAge: 120 });
  const [breakLengthSeconds, setBreakLengthSeconds] = useState(3);
  const [roundStartInstructions, setRoundStartInstructions] = useState({});
  const [branding, setBranding] = useState(getDefaultBranding());

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAdmin) {
      if (VALID_ADMIN_TABS.includes(tabParam)) rememberAdminTab(tabParam);
      const from = `${location.pathname}${location.search}`;
      navigate('/admin/login', { replace: true, state: { from } });
      return undefined;
    }
    loadData();
    runScheduleCheck();
    const scheduleId = setInterval(runScheduleCheck, getScheduleIntervalMs());
    return () => clearInterval(scheduleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    if (VALID_ADMIN_TABS.includes(tabParam)) {
      rememberAdminTab(tabParam);
      return;
    }
    navigate(`/admin/${readRememberedAdminTab()}`, { replace: true });
  }, [authLoading, isAdmin, tabParam, navigate]);

  const loadData = async () => {
    try {
      await loadSettings(); // Load all settings from single document
      await loadCompetitions();
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.languages) {
          setAvailableLanguages(data.languages);
        }
        if (data.minAge !== undefined || data.maxAge !== undefined) {
          setAgeSettings({
            minAge: data.minAge || 0,
            maxAge: data.maxAge || 120
          });
        }
        if (data.breakLengthSeconds !== undefined && data.breakLengthSeconds >= 0) {
          setBreakLengthSeconds(Number(data.breakLengthSeconds));
        }
        if (data.roundStartInstructions && typeof data.roundStartInstructions === 'object') {
          setRoundStartInstructions(data.roundStartInstructions);
        }
        const resolvedBranding = resolveBranding(data);
        setBranding(resolvedBranding);
        if (needsBrandingSeed(data)) {
          await setDoc(doc(db, 'settings', 'global'), resolvedBranding, { merge: true });
        }
      } else {
        // Initialize with defaults if no settings exist
        const defaultSettings = {
          languages: [],
          minAge: 0,
          maxAge: 120,
          adminUUIDs: [FALLBACK_ADMIN_UID],
          breakLengthSeconds: 3
        };
        await setDoc(doc(db, 'settings', 'global'), defaultSettings, { merge: true });
        setAvailableLanguages(defaultSettings.languages);
        setAgeSettings({ minAge: defaultSettings.minAge, maxAge: defaultSettings.maxAge });
        setBreakLengthSeconds(defaultSettings.breakLengthSeconds);
        const resolvedBranding = getDefaultBranding();
        setBranding(resolvedBranding);
        await setDoc(doc(db, 'settings', 'global'), resolvedBranding, { merge: true });
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const saveRoundStartInstructions = async (instructions) => {
    try {
      await setDoc(doc(db, 'settings', 'global'), {
        roundStartInstructions: instructions
      }, { merge: true });
      setRoundStartInstructions(instructions);
    } catch (err) {
      console.error('Error saving round start instructions:', err);
    }
  };

  const saveLanguages = async (newLanguages) => {
    try {
      await setDoc(doc(db, 'settings', 'global'), {
        languages: newLanguages
      }, { merge: true });
      setAvailableLanguages(newLanguages);
    } catch (err) {
      console.error('Error saving languages:', err);
    }
  };

  const saveAgeSettings = async (newAgeSettings) => {
    try {
      await setDoc(doc(db, 'settings', 'global'), {
        minAge: newAgeSettings.minAge,
        maxAge: newAgeSettings.maxAge
      }, { merge: true });
      setAgeSettings(newAgeSettings);
    } catch (err) {
      console.error('Error saving age settings:', err);
    }
  };

  const saveBreakLength = async (seconds) => {
    try {
      const value = Math.max(0, Math.min(120, Number(seconds) || 0));
      await setDoc(doc(db, 'settings', 'global'), {
        breakLengthSeconds: value
      }, { merge: true });
      setBreakLengthSeconds(value);
    } catch (err) {
      console.error('Error saving break length:', err);
    }
  };

  const saveBranding = async (nextBranding) => {
    const contrastProblem = validateThemeColors(nextBranding);
    if (contrastProblem) {
      alert(t('themeContrastError'));
      return;
    }
    try {
      const payload = {
        platformName: nextBranding.platformName || {},
        logoUrl: nextBranding.logoUrl || '',
        joinNotice: nextBranding.joinNotice || {},
        imprintUrl: (nextBranding.imprintUrl || '').trim(),
        privacyUrl: (nextBranding.privacyUrl || '').trim(),
        footerCreditName: (nextBranding.footerCreditName || '').trim(),
        footerCreditUrl: (nextBranding.footerCreditUrl || '').trim(),
        themeId: nextBranding.themeId || 'current',
        primaryColor: nextBranding.primaryColor || '',
        secondaryColor: nextBranding.secondaryColor || '',
        pageBackground: nextBranding.pageBackground || ''
      };
      await setDoc(doc(db, 'settings', 'global'), payload, { merge: true });
      setBranding(resolveBranding(payload));
    } catch (err) {
      console.error('Error saving platform branding:', err);
      alert(t('errorOccurred'));
    }
  };

  const loadCompetitions = async () => {
    try {
      const competitionsRef = collection(db, 'competitions');
      const querySnapshot = await getDocs(competitionsRef);
      const comps = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompetitions(comps);
      
      const active = comps.find(c => c.isActive);
      if (active) {
        setActiveCompetition(active);
        await loadUsers(active.id);
        await loadChallenges(active.id);
        setupRealtimeListeners(active.id);
      }
    } catch (err) {
      console.error('Error loading competitions:', err);
    }
  };

  const setupRealtimeListeners = (competitionId) => {
    // Listen to users
    const usersRef = collection(db, 'competitions', competitionId, 'users');
    const unsubscribeUsers = onSnapshot(usersRef, (snapshot) => {
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    });

    // Listen to challenges
    const challengesRef = collection(db, 'competitions', competitionId, 'challenges');
    const unsubscribeChallenges = onSnapshot(challengesRef, (snapshot) => {
      const challengesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChallenges(challengesList);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeChallenges();
    };
  };

  const loadUsers = async (competitionId) => {
    try {
      const usersRef = collection(db, 'competitions', competitionId, 'users');
      const querySnapshot = await getDocs(usersRef);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadChallenges = async (competitionId) => {
    try {
      const challengesRef = collection(db, 'competitions', competitionId, 'challenges');
      const querySnapshot = await getDocs(challengesRef);
      const challengesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChallenges(challengesList);
    } catch (err) {
      console.error('Error loading challenges:', err);
    }
  };

  const handleCreateCompetition = async (competitionData) => {
    try {
      // Check if there's already an active competition
      const activeComp = competitions.find(c => c.isActive);
      if (activeComp && competitionData.isActive) {
        alert(t('activeCompetitionExists'));
        return;
      }

      // Check for unique name (skip if editing the same competition)
      const nameExists = competitions.some(c => c.name === competitionData.name);
      if (nameExists) {
        alert(t('competitionNameUnique'));
        return;
      }

      const docRef = await addDoc(collection(db, 'competitions'), {
        ...competitionData,
        createdAt: new Date()
      });

      await loadCompetitions();
      return docRef.id;
    } catch (err) {
      console.error('Error creating competition:', err);
      alert(t('errorCreatingCompetition'));
    }
  };

  const handleCloseCompetition = async (competitionId) => {
    try {
      // Get competition data to access rounds
      const competitionDoc = await getDoc(doc(db, 'competitions', competitionId));
      if (!competitionDoc.exists()) {
        console.error('Competition not found');
        return;
      }
      
      const competitionData = competitionDoc.data();
      const rounds = competitionData.rounds || [];
      
      // Find all active round indices before stopping them
      const activeRoundIndices = rounds
        .map((round, index) => round.isActive ? index : null)
        .filter(index => index !== null);
      
      // Stop all active rounds
      const updatedRounds = rounds.map(round => {
        if (round.isActive) {
          return {
            ...round,
            isActive: false,
            hasStarted: true // Mark as started even when stopped
          };
        }
        return round;
      });
      
      // Update competition with stopped rounds and set as inactive; set end time only if not already set
      const updatePayload = {
        isActive: false,
        rounds: updatedRounds
      };
      if (!competitionData.endTime) {
        updatePayload.endTime = serverTimestamp();
      }
      await updateDoc(doc(db, 'competitions', competitionId), updatePayload);
      
      // Set end time for all users in all active rounds that were just stopped
      if (activeRoundIndices.length > 0) {
        // Load users for this competition
        const usersRef = collection(db, 'competitions', competitionId, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const competitionUsers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (competitionUsers.length > 0) {
          const batch = writeBatch(db);
          const endTime = new Date();
          
          competitionUsers.forEach(user => {
            if (!user.frozen) {
              const userRef = doc(db, 'competitions', competitionId, 'users', user.id);
              const roundData = user.roundData || {};
              
              const updatedRoundData = { ...roundData };
              activeRoundIndices.forEach(roundIndex => {
                const currentRoundData = roundData[roundIndex] || {};
                updatedRoundData[roundIndex] = {
                  ...currentRoundData,
                  endTime: endTime
                };
              });
              
              batch.update(userRef, {
                roundData: updatedRoundData
              });
            }
          });
          await batch.commit();
        }
      }
      
      await loadCompetitions();
      setActiveCompetition(null);
      setUsers([]);
      setChallenges([]);
    } catch (err) {
      console.error('Error closing competition:', err);
      alert(t('errorClosingCompetition'));
    }
  };

  const handleUpdateCompetition = async (competitionId, competitionData) => {
    try {
      await updateDoc(doc(db, 'competitions', competitionId), {
        ...competitionData,
        updatedAt: new Date()
      });
      await loadCompetitions();
    } catch (err) {
      console.error('Error updating competition:', err);
      alert(t('errorUpdatingCompetition'));
    }
  };

  const handleReopenCompetition = async (competitionId) => {
    try {
      // Check if there's already an active competition
      const activeComp = competitions.find(c => c.isActive && c.id !== competitionId);
      if (activeComp) {
        alert(t('activeCompetitionExists'));
        return;
      }

      const compDoc = await getDoc(doc(db, 'competitions', competitionId));
      const compData = compDoc.exists() ? compDoc.data() : {};
      const now = new Date();

      // If start/end are set, validate current time is within window; empty means manual only
      const startDate = timestampToDate(compData.startTime);
      const endDate = timestampToDate(compData.endTime);
      if (startDate && now < startDate) {
        alert(t('competitionNotStartedYet') + ' ' + formatTimestamp(compData.startTime));
        return;
      }
      if (endDate && now > endDate) {
        alert(t('competitionAlreadyEnded') + ' ' + formatTimestamp(compData.endTime));
        return;
      }

      const updatePayload = { isActive: true };
      if (!compData.startTime) {
        updatePayload.startTime = serverTimestamp();
      }

      await updateDoc(doc(db, 'competitions', competitionId), updatePayload);
      await loadCompetitions();
    } catch (err) {
      console.error('Error reopening competition:', err);
    }
  };

  const handleDeleteCompetition = async (competitionId) => {
    try {
      const competition = competitions.find(c => c.id === competitionId);
      if (!competition) return;

      // Prevent deleting active competitions
      if (competition.isActive) {
        alert(t('cannotDeleteActiveCompetition'));
        return;
      }

      // Confirmation dialog
      const confirmMessage = t('deleteCompetitionConfirm').replace('{name}', competition.name);
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Delete all subcollections first
      // Delete all users
      const usersRef = collection(db, 'competitions', competitionId, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const userDeletes = usersSnapshot.docs.map(userDoc => 
        deleteDoc(doc(db, 'competitions', competitionId, 'users', userDoc.id))
      );
      await Promise.all(userDeletes);

      // Delete all challenges
      const challengesRef = collection(db, 'competitions', competitionId, 'challenges');
      const challengesSnapshot = await getDocs(challengesRef);
      const challengeDeletes = challengesSnapshot.docs.map(challengeDoc => 
        deleteDoc(doc(db, 'competitions', competitionId, 'challenges', challengeDoc.id))
      );
      await Promise.all(challengeDeletes);

      // Delete the competition document
      await deleteDoc(doc(db, 'competitions', competitionId));
      
      // If this was the selected competition, clear it
      if (activeCompetition && activeCompetition.id === competitionId) {
        setActiveCompetition(null);
        setUsers([]);
        setChallenges([]);
      }
      
      await loadCompetitions();
    } catch (err) {
      console.error('Error deleting competition:', err);
      alert(t('errorDeletingCompetition'));
    }
  };

  const handleFreezeUser = async (userId, frozen) => {
    try {
      await updateDoc(
        doc(db, 'competitions', activeCompetition.id, 'users', userId),
        { frozen }
      );
    } catch (err) {
      console.error('Error freezing user:', err);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      // Confirmation dialog
      const confirmMessage = t('deleteUserConfirm').replace('{name}', userName);
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Delete the user document
      await deleteDoc(doc(db, 'competitions', activeCompetition.id, 'users', userId));
      
      // Reload users
      await loadUsers(activeCompetition.id);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(t('errorDeletingUser'));
    }
  };

  const handleResetUserRound = async (userId, roundIndex) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      const confirmMessage = t('resetRoundConfirm')
        .replace('{name}', user.name)
        .replace('{round}', roundIndex + 1);
      if (!window.confirm(confirmMessage)) {
        return;
      }

      const roundData = user.roundData || {};
      const currentRoundData = roundData[roundIndex] || {};
      
      // Reset round data - clear assigned challenges and challenge states
      const updatedRoundData = {
        ...roundData,
        [roundIndex]: {
          ...currentRoundData,
          startTime: null,
          endTime: null,
          score: 0,
          correctAnswers: 0,
          isDone: false,
          assignedChallenges: [], // Clear assigned challenges on reset
          challengeStates: {}, // Clear challenge states on reset
          totalTimeUsedToSubmit: 0, // Reset per-round time
          correctAnswersCount: 0, // Reset per-round correct answers count
          totalBreakSeconds: 0
        }
      };

      // Remove answers for this round
      const updatedAnswers = (user.answers || []).filter(a => a.round !== roundIndex);
      
      // Recalculate total score
      const totalScore = Object.values(updatedRoundData).reduce((sum, rd) => sum + (rd.score || 0), 0);

      await updateDoc(doc(db, 'competitions', activeCompetition.id, 'users', userId), {
        roundData: updatedRoundData,
        answers: updatedAnswers,
        score: totalScore
      });

      await loadUsers(activeCompetition.id);
    } catch (err) {
      console.error('Error resetting round:', err);
      alert(t('errorResettingRound'));
    }
  };

  const handleStartRound = async (roundIndex) => {
    try {
      const rounds = [...activeCompetition.rounds];
      const now = new Date();
      const thisRound = rounds[roundIndex];

      // Check if any round is currently active
      const hasActiveRound = rounds.some(round => round.isActive === true);
      if (hasActiveRound) {
        alert(t('mustStopActiveRound'));
        return;
      }
      
      // If this round has start/end set, validate current time is within window; empty means manual only
      const roundStartDate = timestampToDate(thisRound?.startTime);
      const roundEndDate = timestampToDate(thisRound?.endTime);
      if (roundStartDate && now < roundStartDate) {
        alert(t('roundNotStartedYet') + ' ' + formatTimestamp(thisRound.startTime));
        return;
      }
      if (roundEndDate && now > roundEndDate) {
        alert(t('roundAlreadyEnded') + ' ' + formatTimestamp(thisRound.endTime));
        return;
      }

      // Check if rounds must be started in order
      if (roundIndex > 0) {
        const previousRound = rounds[roundIndex - 1];
        if (!previousRound.hasStarted && !previousRound.isActive) {
          alert(t('mustStartPreviousRound'));
          return;
        }
        // If both previous round and this round have times set, previous round must end before this round starts
        const prevEndDate = timestampToDate(previousRound?.endTime);
        if (roundStartDate && prevEndDate && prevEndDate > roundStartDate) {
          alert(t('previousRoundMustEndBeforeThisStarts'));
          return;
        }
      }
      
      // Mark this round as started; keep saved start time from creation/edit, else set server timestamp
      rounds[roundIndex] = { 
        ...thisRound, 
        isActive: true,
        hasStarted: true,
        startTime: thisRound.startTime || serverTimestamp()
      };
      
      await updateDoc(doc(db, 'competitions', activeCompetition.id), {
        rounds
      });
      
      // Get all challenges matching the round's categories
      const round = rounds[roundIndex];
      const challengesRef = collection(db, 'competitions', activeCompetition.id, 'challenges');
      const q = query(
        challengesRef,
        where('category', 'in', round.categories || [])
      );
      const challengesSnapshot = await getDocs(q);
      const allChallenges = challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Determine number of challenges to assign
      let numberOfChallenges = 0;
      if (round.type === 'fixed') {
        numberOfChallenges = round.numberOfChallenges || 0;
      } else {
        // For open rounds, assign all available challenges
        numberOfChallenges = allChallenges.length;
      }
      
      // Assign challenges to all users (but don't set startTime - that will be set when user logs in)
      const batch = writeBatch(db);
      users.forEach(user => {
        if (!user.frozen) {
          const userRef = doc(db, 'competitions', activeCompetition.id, 'users', user.id);
          const roundData = user.roundData || {};
          const currentRoundData = roundData[roundIndex] || {};
          
          // Assign random challenges to this user (no repeats per user per round)
          let assignedChallengeIds = [];
          if (allChallenges.length > 0 && numberOfChallenges > 0) {
            assignedChallengeIds = pickAssignedChallengeIds(
              allChallenges,
              numberOfChallenges,
              round.shuffleQuestions !== false
            );
          }
          
          // Initialize challenge states (all start as 'notsolved')
          const challengeStates = {};
          assignedChallengeIds.forEach(id => {
            challengeStates[id] = 'notsolved';
          });
          
          // Initialize round data if not exists
          // Don't set startTime here - it will be set when user first views the round
          // Only preserve existing startTime if it already exists (for users who already started)
          const updatedRoundData = {
            ...roundData,
            [roundIndex]: {
              ...currentRoundData,
              // Only preserve startTime if it already exists, don't set a new one
              startTime: currentRoundData.startTime || null,
              score: currentRoundData.score || 0,
              correctAnswers: currentRoundData.correctAnswers || 0,
              assignedChallenges: assignedChallengeIds, // Store assigned challenge IDs
              challengeStates: challengeStates, // Store challenge states (notsolved, correct, false)
              // Only reset isDone if it's not already true (preserve completed rounds)
              isDone: currentRoundData.isDone === true ? true : false
            }
          };
          
          batch.update(userRef, {
            roundData: updatedRoundData
          });
        }
      });
      await batch.commit();
      
      await loadCompetitions();
    } catch (err) {
      console.error('Error starting round:', err);
      alert(t('errorStartingRound'));
    }
  };

  const handleStopRound = async (roundIndex) => {
    try {
      const rounds = [...activeCompetition.rounds];
      // Preserve hasStarted and startTime when stopping; keep saved end time from creation/edit, else set server timestamp
      const existingRound = rounds[roundIndex];
      rounds[roundIndex] = { 
        ...existingRound, 
        isActive: false,
        hasStarted: true,
        endTime: existingRound.endTime || serverTimestamp()
      };
      
      await updateDoc(doc(db, 'competitions', activeCompetition.id), {
        rounds
      });
      
      // Set end time for all users in this round
      const batch = writeBatch(db);
      const endTime = new Date();
      users.forEach(user => {
        if (!user.frozen) {
          const userRef = doc(db, 'competitions', activeCompetition.id, 'users', user.id);
          const roundData = user.roundData || {};
          const currentRoundData = roundData[roundIndex] || {};
          
          const updatedRoundData = {
            ...roundData,
            [roundIndex]: {
              ...currentRoundData,
              endTime: endTime
            }
          };
          
          batch.update(userRef, {
            roundData: updatedRoundData
          });
        }
      });
      await batch.commit();
      
      await loadCompetitions();
    } catch (err) {
      console.error('Error stopping round:', err);
    }
  };

  if (authLoading || (isAdmin && loading)) {
    return (
      <div className="admin-page" dir={isRtl ? 'rtl' : 'ltr'}>
        <Header />
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-page" dir={isRtl ? 'rtl' : 'ltr'}>
      <Header />
      <div className="admin-container">
        <div className="admin-page-header">
          <div>
            <p className="admin-eyebrow">{t('adminPanel')}</p>
            <h1 className="admin-page-title">{t('admin')}</h1>
          </div>
          {activeCompetition && (
            <div className={`admin-current-comp ${activeCompetition.isActive ? 'is-active' : 'is-inactive'}`}>
              <span className="admin-current-comp-label">{t('selectedCompetition')}</span>
              <span className="admin-current-comp-name">{activeCompetition.name}</span>
              <span className={`status-badge ${activeCompetition.isActive ? 'status-active' : 'status-inactive'}`}>
                {activeCompetition.isActive ? t('active') : t('inactive')}
              </span>
            </div>
          )}
        </div>
        <div className="admin-tabs" role="tablist">
          <button
            type="button"
            className={activeTab === 'competitions' ? 'active' : ''}
            onClick={() => navigate('/admin/competitions')}
          >
            {t('manageCompetitions')}
            <span className="admin-tab-count">{competitions.length}</span>
          </button>
          {activeCompetition && (
            <>
              <button
                type="button"
                className={activeTab === 'users' ? 'active' : ''}
                onClick={() => navigate('/admin/users')}
              >
                {t('users')}
                <span className="admin-tab-count">{users.length}</span>
              </button>
              <button
                type="button"
                className={activeTab === 'challenges' ? 'active' : ''}
                onClick={() => navigate('/admin/challenges')}
              >
                {t('challenge')}
                <span className="admin-tab-count">{challenges.length}</span>
              </button>
              <button
                type="button"
                className={activeTab === 'rounds' ? 'active' : ''}
                onClick={() => navigate('/admin/rounds')}
              >
                {t('rounds')}
                <span className="admin-tab-count">{activeCompetition.rounds?.length || 0}</span>
              </button>
            </>
          )}
          <button
            type="button"
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => navigate('/admin/settings')}
          >
            {t('globalSettings')}
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'competitions' && (
            <CompetitionsTab
              competitions={competitions}
              activeCompetition={activeCompetition}
              onCreateCompetition={handleCreateCompetition}
              onUpdateCompetition={handleUpdateCompetition}
              onCloseCompetition={handleCloseCompetition}
              onReopenCompetition={handleReopenCompetition}
              onDeleteCompetition={handleDeleteCompetition}
              onSelectCompetition={(comp) => {
                setActiveCompetition(comp);
                loadUsers(comp.id);
                loadChallenges(comp.id);
                setupRealtimeListeners(comp.id);
              }}
              t={t}
            />
          )}

          {activeTab === 'settings' && (
            <GlobalSettingsTab
              languages={availableLanguages}
              onSaveLanguages={saveLanguages}
              ageSettings={ageSettings}
              onSaveAgeSettings={saveAgeSettings}
              breakLengthSeconds={breakLengthSeconds}
              onSaveBreakLength={saveBreakLength}
              roundStartInstructions={roundStartInstructions}
              onSaveRoundStartInstructions={saveRoundStartInstructions}
              branding={branding}
              onSaveBranding={saveBranding}
              t={t}
            />
          )}

          {activeTab === 'users' && activeCompetition && (
            <UsersTab
              users={users}
              competition={activeCompetition}
              onFreezeUser={handleFreezeUser}
              onDeleteUser={handleDeleteUser}
              onResetRound={handleResetUserRound}
              t={t}
            />
          )}

          {activeTab === 'challenges' && activeCompetition && (
            <ChallengesTab
              competition={activeCompetition}
              challenges={challenges}
              availableLanguages={availableLanguages}
              onAddChallenge={async (challengeData) => {
                try {
                  await addDoc(
                    collection(db, 'competitions', activeCompetition.id, 'challenges'),
                    challengeData
                  );
                  await loadChallenges(activeCompetition.id);
                } catch (err) {
                  console.error('Error adding challenge:', err);
                }
              }}
              onUpdateChallenge={async (challengeId, challengeData) => {
                try {
                  await updateDoc(
                    doc(db, 'competitions', activeCompetition.id, 'challenges', challengeId),
                    challengeData
                  );
                  await loadChallenges(activeCompetition.id);
                } catch (err) {
                  console.error('Error updating challenge:', err);
                  alert(t('errorUpdatingChallenge') || 'Error updating challenge');
                }
              }}
              onDeleteChallenge={async (challengeId) => {
                try {
                  await deleteDoc(
                    doc(db, 'competitions', activeCompetition.id, 'challenges', challengeId)
                  );
                  await loadChallenges(activeCompetition.id);
                } catch (err) {
                  console.error('Error deleting challenge:', err);
                  alert(t('errorDeletingChallenge') || 'Error deleting challenge');
                }
              }}
              onUpdateCompetition={async (competitionData) => {
                try {
                  await updateDoc(doc(db, 'competitions', activeCompetition.id), competitionData);
                  await loadCompetitions();
                  const updated = competitions.find(c => c.id === activeCompetition.id);
                  if (updated) {
                    setActiveCompetition({ ...updated, ...competitionData });
                  }
                } catch (err) {
                  console.error('Error updating competition:', err);
                }
              }}
              t={t}
            />
          )}

          {activeTab === 'rounds' && activeCompetition && (
            <RoundsTab
              competition={activeCompetition}
              onStartRound={handleStartRound}
              onStopRound={handleStopRound}
              onUpdateRound={async (roundIndex, roundData) => {
                try {
                  const rounds = [...activeCompetition.rounds];
                  rounds[roundIndex] = { ...rounds[roundIndex], ...roundData };
                  await updateDoc(doc(db, 'competitions', activeCompetition.id), {
                    rounds
                  });
                  await loadCompetitions();
                } catch (err) {
                  console.error('Error updating round:', err);
                }
              }}
              t={t}
            />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Competition Management Component
const CompetitionsTab = ({ 
  competitions, 
  activeCompetition, 
  onCreateCompetition,
  onUpdateCompetition,
  onCloseCompetition,
  onReopenCompetition,
  onDeleteCompetition,
  onSelectCompetition,
  t 
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    maxRounds: 0,
    categories: [],
    rounds: [],
    competitionStart: '',
    competitionEnd: ''
  });
  const [newCategory, setNewCategory] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('competitionNameRequired'));
      return;
    }

    if (formData.categories.length === 0) {
      alert(t('categoryRequired'));
      return;
    }

    if (formData.maxRounds < 1) {
      alert(t('minRoundsRequired'));
      return;
    }

    if (formData.rounds.length !== formData.maxRounds) {
      alert(t('configureAllRounds'));
      return;
    }

    const competitionData = {
      name: formData.name.trim(),
      maxRounds: parseInt(formData.maxRounds),
      categories: formData.categories,
      rounds: formData.rounds.map(r => ({
        maxTime: r.maxTime ?? 300,
        categories: r.categories || [],
        type: r.type || 'fixed',
        numberOfChallenges: r.numberOfChallenges ?? 5,
        shuffleQuestions: r.shuffleQuestions !== false,
        shuffleChoices: r.shuffleChoices !== false,
        correctValue: r.correctValue ?? 10,
        wrongValue: r.wrongValue ?? 0,
        isActive: r.isActive === true,
        startTime: (r.startTime && String(r.startTime).trim()) ? Timestamp.fromDate(new Date(r.startTime)) : null,
        endTime: (r.endTime && String(r.endTime).trim()) ? Timestamp.fromDate(new Date(r.endTime)) : null
      })),
      isActive: editingCompetition ? editingCompetition.isActive : true,
      startTime: (formData.competitionStart && String(formData.competitionStart).trim()) ? Timestamp.fromDate(new Date(formData.competitionStart)) : null,
      endTime: (formData.competitionEnd && String(formData.competitionEnd).trim()) ? Timestamp.fromDate(new Date(formData.competitionEnd)) : null
    };

    if (editingCompetition) {
      // Check for unique name (excluding the current competition being edited)
      const nameExists = competitions.some(c => c.name === competitionData.name && c.id !== editingCompetition.id);
      if (nameExists) {
        alert('Competition name must be unique.');
        return;
      }
      await onUpdateCompetition(editingCompetition.id, competitionData);
      setEditingCompetition(null);
    } else {
      // Check for unique name when creating
      const nameExists = competitions.some(c => c.name === competitionData.name);
      if (nameExists) {
        alert('Competition name must be unique.');
        return;
      }
      await onCreateCompetition(competitionData);
    }
    
    setShowCreateForm(false);
    setFormData({
      name: '',
      maxRounds: 0,
      categories: [],
      rounds: [],
      competitionStart: '',
      competitionEnd: ''
    });
  };

  const handleEdit = (comp) => {
    setEditingCompetition(comp);
    const rounds = (comp.rounds || []).map(r => ({
      ...r,
      shuffleQuestions: r.shuffleQuestions !== false,
      shuffleChoices: r.shuffleChoices !== false,
      startTime: toDateTimeLocal(r.startTime) || '',
      endTime: toDateTimeLocal(r.endTime) || ''
    }));
    setFormData({
      name: comp.name,
      maxRounds: comp.maxRounds || 1,
      categories: comp.categories || [],
      rounds,
      competitionStart: toDateTimeLocal(comp.startTime) || '',
      competitionEnd: toDateTimeLocal(comp.endTime) || ''
    });
    setShowCreateForm(true);
    setTimeout(() => {
      document.getElementById('admin-competition-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingCompetition(null);
    setFormData({
      name: '',
      maxRounds: 0,
      categories: [],
      rounds: [],
      competitionStart: '',
      competitionEnd: ''
    });
  };

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      setFormData({
        ...formData,
        categories: [...formData.categories, newCategory.trim()]
      });
      setNewCategory('');
    }
  };

  const removeCategory = (categoryIndex) => {
    const updatedCategories = formData.categories.filter((_, index) => index !== categoryIndex);
    setFormData({
      ...formData,
      categories: updatedCategories
    });
  };

  const updateRounds = () => {
    if (formData.maxRounds <= 0) {
      setFormData(prev => ({ ...prev, rounds: [] }));
      return;
    }
    
    const rounds = [];
    const existingRounds = formData.rounds || [];
    for (let i = 0; i < formData.maxRounds; i++) {
      if (existingRounds[i]) {
        const existing = existingRounds[i];
        rounds.push({
          ...existing,
          startTime: typeof existing.startTime === 'string' ? existing.startTime : (toDateTimeLocal(existing.startTime) || ''),
          endTime: typeof existing.endTime === 'string' ? existing.endTime : (toDateTimeLocal(existing.endTime) || '')
        });
      } else {
        rounds.push({
          maxTime: 300,
          categories: [],
          type: 'fixed',
          numberOfChallenges: 5,
          shuffleQuestions: true,
          shuffleChoices: true,
          correctValue: 10,
          wrongValue: 0,
          isActive: false,
          startTime: '',
          endTime: ''
        });
      }
    }
    setFormData(prev => ({ ...prev, rounds }));
  };

  useEffect(() => {
    if (showCreateForm) {
      updateRounds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.maxRounds, showCreateForm]);

  const [competitionStart, setCompetitionStart] = useState('');
  const [competitionEnd, setCompetitionEnd] = useState('');
  const [savingCompetitionTimes, setSavingCompetitionTimes] = useState(false);
  const [competitionSearch, setCompetitionSearch] = useState('');
  const [competitionStatusFilter, setCompetitionStatusFilter] = useState('all');

  const filteredCompetitions = useMemo(() => {
    const query = competitionSearch.trim().toLowerCase();
    return competitions.filter((comp) => {
      if (competitionStatusFilter === 'active' && !comp.isActive) return false;
      if (competitionStatusFilter === 'inactive' && comp.isActive) return false;
      if (!query) return true;
      const haystack = [comp.name, ...(comp.categories || [])].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [competitions, competitionSearch, competitionStatusFilter]);

  const competitionFiltersActive = Boolean(competitionSearch.trim()) || competitionStatusFilter !== 'all';

  useEffect(() => {
    if (activeCompetition) {
      setCompetitionStart(toDateTimeLocal(activeCompetition.startTime));
      setCompetitionEnd(toDateTimeLocal(activeCompetition.endTime));
    }
  }, [activeCompetition]);

  const handleSaveCompetitionTimes = async () => {
    if (!activeCompetition) return;
    setSavingCompetitionTimes(true);
    try {
      await onUpdateCompetition(activeCompetition.id, {
        startTime: competitionStart ? Timestamp.fromDate(new Date(competitionStart)) : null,
        endTime: competitionEnd ? Timestamp.fromDate(new Date(competitionEnd)) : null
      });
    } finally {
      setSavingCompetitionTimes(false);
    }
  };

  return (
    <div className="competitions-tab">
      <div className="admin-section-header">
        <div>
          <h2>{t('manageCompetitions')}</h2>
          <p className="admin-section-subtitle">{t('allCompetitions')}</p>
        </div>
        {!showCreateForm && (
          <button
            className="create-button"
            onClick={() => {
              setEditingCompetition(null);
              setFormData({
                name: '',
                maxRounds: 0,
                categories: [],
                rounds: [],
                competitionStart: '',
                competitionEnd: ''
              });
              setShowCreateForm(true);
            }}
            disabled={activeCompetition?.isActive === true}
          >
            {t('createCompetition')}
          </button>
        )}
      </div>
      
      {activeCompetition && activeCompetition.isActive && (
        <div className="active-competition-info">
          <div>
            <p className="admin-banner-kicker">{t('active')}</p>
            <h3>{activeCompetition.name}</h3>
          </div>
          <button
            className="close-button"
            onClick={() => onCloseCompetition(activeCompetition.id)}
          >
            {t('close')}
          </button>
        </div>
      )}
      
      {activeCompetition && !activeCompetition.isActive && (
        <div className="inactive-competition-info">
          <div>
            <p className="admin-banner-kicker">{t('inactive')}</p>
            <h3>{activeCompetition.name}</h3>
          </div>
        </div>
      )}

      {activeCompetition && (
        <div className="competition-period-edit card">
          <h4>{t('competitionPeriod')}</h4>
          <p className="period-hint">{t('setCompetitionPeriodHint')}</p>
          <div className="period-fields">
            <div className="form-group">
              <label>{t('startTimeLabel')}</label>
              <input
                type="datetime-local"
                value={competitionStart}
                onChange={(e) => setCompetitionStart(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('endTimeLabel')}</label>
              <input
                type="datetime-local"
                value={competitionEnd}
                onChange={(e) => setCompetitionEnd(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="save-times-button"
              onClick={handleSaveCompetitionTimes}
              disabled={savingCompetitionTimes}
            >
              {savingCompetitionTimes ? t('loading') : t('saveTimes')}
            </button>
          </div>
        </div>
      )}

      <div className="competitions-list">
        <h3>{t('allCompetitions')}</h3>
        <AdminListToolbar
          searchValue={competitionSearch}
          onSearchChange={setCompetitionSearch}
          searchPlaceholder={t('searchCompetitionsPlaceholder')}
          shownCount={filteredCompetitions.length}
          totalCount={competitions.length}
          t={t}
          showClear={competitionFiltersActive}
          onClear={() => {
            setCompetitionSearch('');
            setCompetitionStatusFilter('all');
          }}
          filters={[
            {
              id: 'status',
              label: t('filterStatus'),
              value: competitionStatusFilter,
              onChange: setCompetitionStatusFilter,
              options: [
                { value: 'all', label: t('all') },
                { value: 'active', label: t('active') },
                { value: 'inactive', label: t('inactive') }
              ]
            }
          ]}
        />
        {filteredCompetitions.length === 0 ? (
          <div className="admin-empty-state">
            <p>{competitions.length === 0 ? t('noCompetitionsYet') : t('noMatchingResults')}</p>
          </div>
        ) : (
          filteredCompetitions.map(comp => (
            <div
              key={comp.id}
              className={`competition-item ${comp.isActive ? 'is-active' : ''} ${activeCompetition?.id === comp.id ? 'is-selected' : ''}`}
            >
              <div className="competition-item-main">
                <div className="competition-item-title-row">
                  <span className="competition-item-name">{comp.name}</span>
                  <span className={`status-badge ${comp.isActive ? 'status-active' : 'status-inactive'}`}>
                    {comp.isActive ? t('active') : t('inactive')}
                  </span>
                </div>
                <div className="competition-item-meta">
                  <span>{t('rounds')}: {comp.maxRounds || comp.rounds?.length || 0}</span>
                  {comp.categories?.length > 0 && (
                    <span>{t('category')}: {comp.categories.join(', ')}</span>
                  )}
                </div>
              </div>
              <div className="competition-item-actions">
                {!comp.isActive && (
                  <button className="competition-reopen-button" onClick={() => onReopenCompetition(comp.id)}>
                    {t('reopen')}
                  </button>
                )}
                <button onClick={() => handleEdit(comp)}>
                  {t('edit')}
                </button>
                <button onClick={() => onSelectCompetition(comp)}>
                  {t('view')}
                </button>
                <button
                  className="competition-delete-button"
                  onClick={() => onDeleteCompetition(comp.id)}
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateForm && (
        <AdminModal
          wide
          title={editingCompetition ? `${t('edit')}: ${editingCompetition.name}` : t('createCompetition')}
          onClose={handleCancel}
        >
        <form id="admin-competition-form" onSubmit={handleSubmit} className="create-competition-form">
          {editingCompetition && (
            <div className="edit-notice">
              <strong>{t('edit')}: {editingCompetition.name}</strong>
            </div>
          )}
          <div className="form-group">
            <label>{t('competitionName')} <span className="required-indicator">*</span></label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('maxRounds')} <span className="required-indicator">*</span></label>
            <input
              type="number"
              min="1"
              value={formData.maxRounds || 0}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setFormData({ ...formData, maxRounds: value });
              }}
              required
            />
            {formData.maxRounds < 1 && (
              <span style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem', display: 'block' }}>
                {t('minRoundsRequired')}
              </span>
            )}
          </div>

          <div className="form-group period-fields">
            <label>{t('competitionPeriod')}</label>
            <p className="period-hint">{t('setCompetitionPeriodHint')}</p>
            <div className="period-fields-inline">
              <div className="form-group">
                <label>{t('startTimeLabel')}</label>
                <input
                  type="datetime-local"
                  value={formData.competitionStart || ''}
                  onChange={(e) => setFormData({ ...formData, competitionStart: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('endTimeLabel')}</label>
                <input
                  type="datetime-local"
                  value={formData.competitionEnd || ''}
                  onChange={(e) => setFormData({ ...formData, competitionEnd: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>{t('challengeCategories')}</label>
            <div className="categories-input">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder={t('categoryName')}
              />
              <button type="button" onClick={addCategory}>{t('addCategory')}</button>
            </div>
            <div className="categories-list">
              {formData.categories.map((cat, index) => (
                <span key={index} className="category-tag">
                  <input
                    type="text"
                    value={cat}
                    onChange={(e) => {
                      const updatedCategories = [...formData.categories];
                      updatedCategories[index] = e.target.value;
                      setFormData({ ...formData, categories: updatedCategories });
                    }}
                    onBlur={(e) => {
                      // Remove empty categories
                      if (!e.target.value.trim()) {
                        removeCategory(index);
                      }
                    }}
                    style={{ border: 'none', background: 'transparent', padding: '0.2rem', minWidth: '100px', outline: 'none' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => removeCategory(index)}
                    title={t('remove') || 'Remove'}
                    style={{ 
                      cursor: 'pointer',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      lineHeight: '1'
                    }}
                  >×</button>
                </span>
              ))}
            </div>
          </div>

          {formData.maxRounds > 0 && (
            <div className="rounds-config">
              <h4>{t('roundSettings')}</h4>
              {formData.rounds.map((round, index) => (
              <RoundConfigForm
                key={index}
                roundIndex={index}
                round={round}
                categories={formData.categories}
                onUpdate={(roundData) => {
                  const rounds = [...formData.rounds];
                  rounds[index] = { ...rounds[index], ...roundData };
                  setFormData({ ...formData, rounds });
                }}
                t={t}
              />
            ))}
            </div>
          )}

          <div className="form-actions">
            <button type="submit">{t('save')}</button>
            <button type="button" onClick={handleCancel}>
              {t('cancel')}
            </button>
          </div>
        </form>
        </AdminModal>
      )}
    </div>
  );
};

// Round Configuration Component
const RoundConfigForm = ({ roundIndex, round, categories, onUpdate, t }) => {
  const roundStart = typeof round.startTime === 'string' ? round.startTime : (toDateTimeLocal(round.startTime) || '');
  const roundEnd = typeof round.endTime === 'string' ? round.endTime : (toDateTimeLocal(round.endTime) || '');
  return (
    <div className="round-config">
      <h5>{t('round')} {roundIndex + 1}</h5>
      <div className="form-group">
        <label>{t('maxTimePerRound')}</label>
        <input
          type="number"
          min="1"
          value={round.maxTime || 300}
          onChange={(e) => onUpdate({ maxTime: parseInt(e.target.value) || 300 })}
        />
      </div>
      <div className="form-group">
        <label>{t('roundPeriod')}</label>
        <p className="period-hint">{t('setRoundPeriodHint')}</p>
        <div className="period-fields-inline">
          <div className="form-group">
            <label>{t('startTimeLabel')}</label>
            <input
              type="datetime-local"
              value={roundStart}
              onChange={(e) => onUpdate({ startTime: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('endTimeLabel')}</label>
            <input
              type="datetime-local"
              value={roundEnd}
              onChange={(e) => onUpdate({ endTime: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="form-group">
        <label>{t('roundType')}</label>
        <select
          value={round.type || 'fixed'}
          onChange={(e) => onUpdate({ type: e.target.value })}
        >
          <option value="fixed">{t('fixedChallenges')}</option>
          <option value="open">{t('openChallenges')}</option>
        </select>
      </div>
      {round.type === 'fixed' && (
        <div className="form-group">
          <label>{t('numberOfChallenges')}</label>
          <input
            type="number"
            min="1"
            value={round.numberOfChallenges || 5}
            onChange={(e) => onUpdate({ numberOfChallenges: parseInt(e.target.value) || 5 })}
          />
        </div>
      )}
      <div className="form-group">
        <label>{t('questionOrder')}</label>
        <select
          value={round.shuffleQuestions === false ? 'upload' : 'random'}
          onChange={(e) => onUpdate({ shuffleQuestions: e.target.value === 'random' })}
        >
          <option value="random">{t('orderRandomPerUser')}</option>
          <option value="upload">{t('orderUploadOrder')}</option>
        </select>
      </div>
      <div className="form-group">
        <label>{t('choiceOrder')}</label>
        <select
          value={round.shuffleChoices === false ? 'upload' : 'random'}
          onChange={(e) => onUpdate({ shuffleChoices: e.target.value === 'random' })}
        >
          <option value="random">{t('orderRandomPerUser')}</option>
          <option value="upload">{t('orderUploadOrder')}</option>
        </select>
      </div>
      <div className="form-group">
        <label>{t('selectCategories')}</label>
        <div className="categories-checkboxes">
          {categories.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No challenge categories available. Please add categories first.</p>
          ) : (
            categories.map(cat => {
              const isSelected = round.categories?.includes(cat) || false;
              return (
                <label key={cat} style={{ 
                  backgroundColor: isSelected ? '#e3f2fd' : '#fff',
                  borderColor: isSelected ? 'var(--brand-primary)' : '#ddd',
                  fontWeight: isSelected ? '500' : '400'
                }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const currentCategories = round.categories || [];
                      if (e.target.checked) {
                        onUpdate({ categories: [...currentCategories, cat] });
                      } else {
                        onUpdate({ categories: currentCategories.filter(c => c !== cat) });
                      }
                    }}
                  />
                  <span>{cat}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
      <div className="form-group">
        <label>{t('correctSolutionValue')}</label>
        <input
          type="number"
          value={round.correctValue || 10}
          onChange={(e) => onUpdate({ correctValue: parseInt(e.target.value) || 10 })}
        />
      </div>
      <div className="form-group">
        <label>{t('wrongSolutionValue')}</label>
        <input
          type="number"
          value={round.wrongValue || 0}
          onChange={(e) => onUpdate({ wrongValue: parseInt(e.target.value) || 0 })}
        />
      </div>
    </div>
  );
};

// Global Settings Tab Component
const GlobalSettingsTab = ({ languages, onSaveLanguages, ageSettings, onSaveAgeSettings, breakLengthSeconds, onSaveBreakLength, roundStartInstructions, onSaveRoundStartInstructions, branding, onSaveBranding, t }) => {
  const [newLanguage, setNewLanguage] = useState({ shortcut: '', name: '' });
  const [localAgeSettings, setLocalAgeSettings] = useState(ageSettings || { minAge: 0, maxAge: 120 });
  const [localBreakLength, setLocalBreakLength] = useState(breakLengthSeconds ?? 3);
  const [localRoundStartInstructions, setLocalRoundStartInstructions] = useState(roundStartInstructions || {}); // eslint-disable-line no-unused-vars -- used in JSX value and onSaveRoundStartInstructions
  const [languageSearch, setLanguageSearch] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminBusy, setAdminBusy] = useState('');
  const [adminNotice, setAdminNotice] = useState('');
  const [ownPassword, setOwnPassword] = useState({ current: '', next: '', confirm: '' });
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState('');
  const [localBranding, setLocalBranding] = useState(branding || getDefaultBranding());
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);
  const [languageError, setLanguageError] = useState('');
  const activeThemeId = localBranding.themeId === 'current' ? 'default' : localBranding.themeId;

  useEffect(() => {
    setLocalAgeSettings(ageSettings || { minAge: 0, maxAge: 120 });
  }, [ageSettings]);

  useEffect(() => {
    setLocalBreakLength(breakLengthSeconds ?? 3);
  }, [breakLengthSeconds]);

  useEffect(() => {
    setLocalRoundStartInstructions(roundStartInstructions || {});
  }, [roundStartInstructions]);

  useEffect(() => {
    setLocalBranding(branding || getDefaultBranding());
  }, [branding]);

  useEffect(() => {
    let active = true;
    callManageAdmin({ action: 'list' })
      .then((result) => {
        if (active) setAdminAccounts(result.accounts || []);
      })
      .catch((err) => {
        console.error('Error loading admin accounts:', err);
        if (active) setAdminNotice('errorOccurred');
      });
    return () => {
      active = false;
    };
  }, []);

  const handleAdd = () => {
    const shortcut = newLanguage.shortcut.trim().toLowerCase();
    const name = newLanguage.name.trim();
    if (!/^[a-z]{2,5}$/.test(shortcut)) {
      setLanguageError(t('invalidLanguageShortcut'));
      return;
    }
    if (!name) {
      setLanguageError(t('invalidLanguageShortcut'));
      return;
    }
    if (languages.some((lang) => String(lang.shortcut || '').toLowerCase() === shortcut)) {
      setLanguageError(t('languageAlreadyAdded'));
      return;
    }
    onSaveLanguages([...languages, { shortcut, name }]);
    setNewLanguage({ shortcut: '', name: '' });
    setLanguageError('');
  };

  const handleDelete = (index) => {
    const updated = languages.filter((_, i) => i !== index);
    onSaveLanguages(updated);
  };

  const refreshAdmins = (accounts) => {
    setAdminAccounts(accounts || []);
  };

  const handleAddAdmin = async () => {
    const email = adminEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAdminNotice('adminEmailRequired');
      return;
    }
    if (adminPassword.length < 8) {
      setAdminNotice('passwordTooShort');
      return;
    }
    setAdminBusy('create');
    setAdminNotice('');
    try {
      const result = await callManageAdmin({
        action: 'create',
        email,
        password: adminPassword
      });
      refreshAdmins(result.accounts);
      setAdminEmail('');
      setAdminPassword('');
      if (result.status === 'already-admin') {
        setAdminNotice('adminAlreadyExists');
      } else if (result.status === 'existing-added') {
        setAdminNotice('adminExistingAdded');
      } else {
        setAdminNotice('adminAdded');
      }
    } catch (err) {
      console.error('Error adding admin:', err);
      setAdminNotice(adminAccountErrorKey(err));
    } finally {
      setAdminBusy('');
    }
  };

  const handleRemoveAdmin = async (account) => {
    if (!window.confirm(t('removeAdminConfirm'))) return;
    setAdminBusy(account.uid);
    setAdminNotice('');
    try {
      const result = await callManageAdmin({ action: 'remove', uid: account.uid });
      refreshAdmins(result.accounts);
    } catch (err) {
      console.error('Error removing admin:', err);
      setAdminNotice(adminAccountErrorKey(err));
    } finally {
      setAdminBusy('');
    }
  };

  const handlePasswordChange = async () => {
    if (ownPassword.next.length < 8) {
      setPasswordNotice('passwordTooShort');
      return;
    }
    if (ownPassword.next !== ownPassword.confirm) {
      setPasswordNotice('passwordMismatch');
      return;
    }
    setPasswordBusy(true);
    setPasswordNotice('');
    try {
      await changeOwnPassword(ownPassword.current, ownPassword.next);
      setOwnPassword({ current: '', next: '', confirm: '' });
      setPasswordNotice('passwordChanged');
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordNotice(adminAccountErrorKey(err));
    } finally {
      setPasswordBusy(false);
    }
  };

  const updateLocalizedBranding = (field, lang, value) => {
    setLocalBranding((prev) => ({
      ...prev,
      [field]: {
        ...(prev[field] || {}),
        [lang]: value
      }
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert(t('invalidImageFile'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('imageFileTooLarge'));
      return;
    }

    setUploadingLogo(true);
    setLogoProgress(0);
    try {
      const fileName = `branding/logo_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, file);
      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            setLogoProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          },
          reject,
          async () => {
            try {
              resolve(await getDownloadURL(uploadTask.snapshot.ref));
            } catch (error) {
              reject(error);
            }
          }
        );
      });
      setLocalBranding((prev) => ({ ...prev, logoUrl: downloadURL }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert(t('errorUploadingImage'));
    } finally {
      setUploadingLogo(false);
      setLogoProgress(0);
      e.target.value = '';
    }
  };

  return (
    <div className="global-settings-tab">
      <div className="global-settings-header">
        <h2>{t('globalSettings')}</h2>
        <p className="settings-section-description">{t('globalSettingsIntro')}</p>
      </div>
      <nav className="settings-jump-nav" aria-label={t('globalSettings')}>
        {[
          ['settings-identity', 'platformIdentity'],
          ['settings-languages', 'languages'],
          ['settings-age', 'ageSettings'],
          ['settings-break', 'breakLengthSeconds'],
          ['settings-round-start', 'roundStartInstructionsLabel'],
          ['settings-password', 'changeOwnPassword'],
          ['settings-admins', 'adminManagement']
        ].map(([id, labelKey]) => (
          <button
            key={id}
            type="button"
            onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {t(labelKey)}
          </button>
        ))}
      </nav>

      <div className="settings-section" id="settings-identity">
        <div className="settings-section-header">
          <h3>{t('platformIdentity')}</h3>
          <p className="settings-section-description">{t('platformIdentityDescription')}</p>
        </div>

        <div className="branding-settings-form">
          <div className="settings-subsection">
          <h4 className="languages-list-title">{t('platformName')}</h4>
          <div className="localized-fields">
          {(languages.length > 0 ? languages : [{ shortcut: 'en', name: 'English' }]).map((lang) => (
            <div key={`platform-name-${lang.shortcut}`} className="form-input-group">
              <label htmlFor={`platform-name-${lang.shortcut}`}>
                {lang.name} ({lang.shortcut})
              </label>
              <input
                id={`platform-name-${lang.shortcut}`}
                type="text"
                value={localBranding.platformName?.[lang.shortcut] || ''}
                onChange={(e) => updateLocalizedBranding('platformName', lang.shortcut, e.target.value)}
                placeholder={t('platformName')}
              />
            </div>
          ))}
          </div>
          </div>

          <div className="settings-subsection">
          <div className="form-input-group">
            <label>{t('platformLogo')}</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploadingLogo}
            />
            {uploadingLogo && (
              <small>{Math.round(logoProgress)}% {t('uploading')}</small>
            )}
            {localBranding.logoUrl && !uploadingLogo && (
              <div className="branding-logo-preview">
                <img
                  src={localBranding.logoUrl}
                  alt={t('platformLogo')}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  className="delete-language-button"
                  onClick={() => setLocalBranding((prev) => ({ ...prev, logoUrl: '' }))}
                >
                  {t('remove')}
                </button>
              </div>
            )}
          </div>
          </div>

          <div className="settings-subsection">
          <h4 className="languages-list-title">{t('joinNotice')}</h4>
          <p className="settings-section-description">{t('joinNoticeDescription')}</p>
          <div className="localized-fields">
          {(languages.length > 0 ? languages : [{ shortcut: 'en', name: 'English' }]).map((lang) => (
            <div key={`join-notice-${lang.shortcut}`} className="form-input-group round-start-instruction-lang">
              <label htmlFor={`join-notice-${lang.shortcut}`}>
                {lang.name} ({lang.shortcut})
              </label>
              <textarea
                id={`join-notice-${lang.shortcut}`}
                rows={4}
                value={localBranding.joinNotice?.[lang.shortcut] || ''}
                onChange={(e) => updateLocalizedBranding('joinNotice', lang.shortcut, e.target.value)}
                placeholder={t('joinNotice')}
              />
            </div>
          ))}
          </div>
          </div>

          <div className="settings-subsection">
          <h4 className="languages-list-title">{t('legalLinks')}</h4>
          <p className="settings-section-description">{t('legalLinksDescription')}</p>
          <div className="settings-field-grid">
          <div className="form-input-group">
            <label htmlFor="imprintUrl">{t('imprintUrl')}</label>
            <input
              id="imprintUrl"
              type="url"
              value={localBranding.imprintUrl || ''}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, imprintUrl: e.target.value }))}
              placeholder="https://"
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="privacyUrl">{t('privacyUrl')}</label>
            <input
              id="privacyUrl"
              type="url"
              value={localBranding.privacyUrl || ''}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, privacyUrl: e.target.value }))}
              placeholder="https://"
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="footerCreditName">{t('footerCreditName')}</label>
            <input
              id="footerCreditName"
              type="text"
              value={localBranding.footerCreditName || ''}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, footerCreditName: e.target.value }))}
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="footerCreditUrl">{t('footerCreditUrl')}</label>
            <input
              id="footerCreditUrl"
              type="url"
              value={localBranding.footerCreditUrl || ''}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, footerCreditUrl: e.target.value }))}
              placeholder="https://"
            />
          </div>
          </div>
          </div>

          <div className="settings-subsection">
          <h4 className="languages-list-title">{t('platformColors')}</h4>
          <p className="settings-section-description">{t('platformColorsDescription')}</p>
          <div className="theme-preset-grid">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`theme-preset ${activeThemeId === preset.id ? 'is-selected' : ''}`}
                onClick={() => setLocalBranding((prev) => ({
                  ...prev,
                  themeId: preset.id,
                  primaryColor: preset.primaryColor,
                  secondaryColor: preset.secondaryColor,
                  pageBackground: preset.pageBackground
                }))}
              >
                <span className="theme-preset-swatches" aria-hidden="true">
                  <span style={{ background: preset.primaryColor }} />
                  <span style={{ background: preset.secondaryColor }} />
                </span>
                {t(preset.labelKey)}
              </button>
            ))}
          </div>
          <div className="theme-color-fields">
            <label className="theme-color-field">
              <span>{t('primaryColor')}</span>
              <input
                type="color"
                value={normalizeHex(localBranding.primaryColor) || '#3965ac'}
                onChange={(e) => setLocalBranding((prev) => ({
                  ...prev,
                  primaryColor: e.target.value,
                  themeId: themeFromColors(e.target.value, prev.secondaryColor, prev.pageBackground)
                }))}
              />
            </label>
            <label className="theme-color-field">
              <span>{t('secondaryColor')}</span>
              <input
                type="color"
                value={normalizeHex(localBranding.secondaryColor) || '#be9830'}
                onChange={(e) => setLocalBranding((prev) => ({
                  ...prev,
                  secondaryColor: e.target.value,
                  themeId: themeFromColors(prev.primaryColor, e.target.value, prev.pageBackground)
                }))}
              />
            </label>
            <label className="theme-color-field">
              <span>{t('pageBackground')}</span>
              <input
                type="color"
                value={normalizeHex(localBranding.pageBackground) || '#eef2f7'}
                onChange={(e) => setLocalBranding((prev) => ({
                  ...prev,
                  pageBackground: e.target.value,
                  themeId: themeFromColors(prev.primaryColor, prev.secondaryColor, e.target.value)
                }))}
              />
            </label>
          </div>
          <label className="theme-gradient-toggle">
            <input
              type="checkbox"
              checked={!localBranding.pageBackground}
              onChange={(e) => setLocalBranding((prev) => {
                const pageBackground = e.target.checked ? '' : '#eef2f7';
                return {
                  ...prev,
                  pageBackground,
                  themeId: themeFromColors(prev.primaryColor, prev.secondaryColor, pageBackground)
                };
              })}
            />
            <span>{t('useDefaultPageGradient')}</span>
          </label>
          <p className="settings-section-description">{t('themeFixedColorsNote')}</p>

          <button
            type="button"
            className="save-age-settings-button"
            onClick={() => onSaveBranding(localBranding)}
          >
            {t('save')}
          </button>
          </div>
        </div>
      </div>
      
      <div className="settings-section" id="settings-languages">
        <div className="settings-section-header">
          <h3>{t('languages')}</h3>
          <p className="settings-section-description">{t('manageLanguagesDescription')}</p>
        </div>

        <div className="language-guide">
          <h4>{t('languageGuideTitle')}</h4>
          <p>{t('languageGuideShortcut')}</p>
          <p>{t('languageGuideReady')}</p>
          <div className="language-ready-list">
            {bundledLanguages.map((lang) => {
              const alreadyAdded = languages.some((item) => String(item.shortcut || '').toLowerCase() === lang.shortcut);
              return (
                <button
                  key={lang.shortcut}
                  type="button"
                  className={`language-ready-chip ${alreadyAdded ? 'is-added' : ''}`}
                  onClick={() => {
                    if (alreadyAdded) return;
                    setNewLanguage({ shortcut: lang.shortcut, name: lang.name });
                    setLanguageError('');
                  }}
                >
                  <span>{lang.shortcut}</span>
                  {lang.name}
                </button>
              );
            })}
          </div>
          <p>{t('languageGuideExtend')}</p>
          <p>{t('languageGuideMissing')}</p>
        </div>
        
        <div className="add-language-form">
          <div className="form-input-group">
            <label htmlFor="language-shortcut">{t('languageShortcut')}</label>
            <input
              id="language-shortcut"
              type="text"
              placeholder={t('languageShortcut')}
              value={newLanguage.shortcut}
              onChange={(e) => {
                setLanguageError('');
                setNewLanguage({ ...newLanguage, shortcut: e.target.value.toLowerCase() });
              }}
              maxLength={5}
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="language-name">{t('languageName')}</label>
            <input
              id="language-name"
              type="text"
              placeholder={t('languageName')}
              value={newLanguage.name}
              onChange={(e) => setNewLanguage({ ...newLanguage, name: e.target.value })}
            />
          </div>
          <button 
            className="add-language-button"
            onClick={handleAdd}
            disabled={!newLanguage.shortcut.trim() || !newLanguage.name.trim()}
          >
            {t('addLanguage')}
          </button>
        </div>
        {languageError && <p className="settings-inline-error">{languageError}</p>}
        
        {languages.length > 0 ? (
          <div className="languages-list">
            <h4 className="languages-list-title">{t('availableLanguages')} ({languages.length})</h4>
            <AdminListToolbar
              searchValue={languageSearch}
              onSearchChange={setLanguageSearch}
              searchPlaceholder={t('searchPlaceholder')}
              shownCount={languages.filter((lang) => `${lang.shortcut} ${lang.name}`.toLowerCase().includes(languageSearch.trim().toLowerCase())).length}
              totalCount={languages.length}
              t={t}
              showClear={Boolean(languageSearch.trim())}
              onClear={() => setLanguageSearch('')}
            />
            {languages
              .map((lang, index) => ({ lang, index }))
              .filter(({ lang }) => `${lang.shortcut} ${lang.name}`.toLowerCase().includes(languageSearch.trim().toLowerCase()))
              .map(({ lang, index }) => (
              <div key={index} className="language-item">
                <div className="language-item-content">
                  <span className="language-shortcut">{lang.shortcut}</span>
                  <span className="language-name">{lang.name}</span>
                  <span className={`language-file-badge ${BUNDLED_LANGUAGE_CODES.has(String(lang.shortcut).toLowerCase()) ? 'is-ready' : ''}`}>
                    {BUNDLED_LANGUAGE_CODES.has(String(lang.shortcut).toLowerCase()) ? t('languageInterfaceReady') : t('languageContentOnly')}
                  </span>
                </div>
                <button 
                  className="delete-language-button"
                  onClick={() => handleDelete(index)}
                  title={t('delete')}
                >
                  {t('delete')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-languages-message">
            <p>{t('noLanguagesAdded')}</p>
          </div>
        )}
      </div>

      <div className="settings-section" id="settings-age">
        <div className="settings-section-header">
          <h3>{t('ageSettings')}</h3>
          <p className="settings-section-description">{t('ageSettingsDescription')}</p>
        </div>
        
        <div className="age-settings-form">
          <div className="form-input-group">
            <label htmlFor="minAge">{t('minAge')} <span className="required-indicator">*</span></label>
            <input
              id="minAge"
              type="number"
              min="0"
              max="120"
              value={localAgeSettings.minAge || 0}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setLocalAgeSettings({ ...localAgeSettings, minAge: value });
              }}
              required
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="maxAge">{t('maxAge')} <span className="required-indicator">*</span></label>
            <input
              id="maxAge"
              type="number"
              min="0"
              value={localAgeSettings.maxAge || 120}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 120;
                setLocalAgeSettings({ ...localAgeSettings, maxAge: value });
              }}
              required
            />
          </div>
          <button 
            className="save-age-settings-button"
            onClick={() => onSaveAgeSettings(localAgeSettings)}
          >
            {t('save')}
          </button>
        </div>
      </div>

      <div className="settings-section" id="settings-break">
        <div className="settings-section-header">
          <h3>{t('breakLengthSeconds')}</h3>
          <p className="settings-section-description">{t('breakLengthDescription')}</p>
        </div>
        <div className="break-length-form">
          <div className="form-input-group">
            <label htmlFor="breakLengthSeconds">{t('breakLengthSeconds')} (1–120)</label>
            <input
              id="breakLengthSeconds"
              type="number"
              min="0"
              max="120"
              value={localBreakLength}
              onChange={(e) => setLocalBreakLength(Math.max(0, Math.min(120, parseInt(e.target.value, 10) || 0)))}
            />
          </div>
          <button
            className="save-break-length-button"
            onClick={() => onSaveBreakLength(localBreakLength)}
          >
            {t('save')}
          </button>
        </div>
      </div>

      <div className="settings-section" id="settings-round-start">
        <div className="settings-section-header">
          <h3>{t('roundStartInstructionsLabel')}</h3>
          <p className="settings-section-description">{t('roundStartInstructionsDescription')}</p>
        </div>
        <div className="round-start-instructions-form">
          {languages.map((lang) => (
            <div key={lang.shortcut} className="form-input-group round-start-instruction-lang">
              <label htmlFor={`round-start-${lang.shortcut}`}>{lang.name} ({lang.shortcut})</label>
              <textarea
                id={`round-start-${lang.shortcut}`}
                rows={4}
                value={localRoundStartInstructions[lang.shortcut] ?? ''}
                onChange={(e) => setLocalRoundStartInstructions((prev) => ({ ...prev, [lang.shortcut]: e.target.value }))}
                placeholder={t('roundStartInstructionsDefault')}
              />
            </div>
          ))}
          <button
            className="save-break-length-button"
            onClick={() => onSaveRoundStartInstructions(localRoundStartInstructions)}
          >
            {t('save')}
          </button>
        </div>
      </div>

      <div className="settings-section" id="settings-password">
        <div className="settings-section-header">
          <h3>{t('changeOwnPassword')}</h3>
          <p className="settings-section-description">{t('changeOwnPasswordDescription')}</p>
        </div>
        <div className="add-admin-form">
          <div className="form-input-group">
            <label htmlFor="currentPassword">{t('currentPassword')}</label>
            <input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={ownPassword.current}
              onChange={(e) => setOwnPassword((prev) => ({ ...prev, current: e.target.value }))}
            />
          </div>
          <div className="settings-field-grid">
            <div className="form-input-group">
              <label htmlFor="newPassword">{t('newPassword')}</label>
              <input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                value={ownPassword.next}
                onChange={(e) => setOwnPassword((prev) => ({ ...prev, next: e.target.value }))}
              />
            </div>
            <div className="form-input-group">
              <label htmlFor="confirmPassword">{t('confirmPassword')}</label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={ownPassword.confirm}
                onChange={(e) => setOwnPassword((prev) => ({ ...prev, confirm: e.target.value }))}
              />
            </div>
          </div>
          {passwordNotice && <p className="settings-form-notice">{t(passwordNotice)}</p>}
          <button
            type="button"
            className="add-admin-button"
            onClick={handlePasswordChange}
            disabled={passwordBusy || !ownPassword.current || !ownPassword.next || !ownPassword.confirm}
          >
            {passwordBusy ? t('saving') : t('changeOwnPassword')}
          </button>
        </div>
      </div>

      <div className="settings-section" id="settings-admins">
        <div className="settings-section-header">
          <h3>{t('adminManagement')}</h3>
          <p className="settings-section-description">{t('adminManagementDescription')}</p>
        </div>

        <div className="add-admin-form">
          <div className="form-input-group">
            <label htmlFor="adminEmail">{t('adminEmail')}</label>
            <input
              id="adminEmail"
              type="email"
              autoComplete="off"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </div>
          <div className="form-input-group">
            <label htmlFor="adminPassword">{t('adminPassword')}</label>
            <input
              id="adminPassword"
              type="password"
              autoComplete="new-password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
          </div>
          {adminNotice && <p className="settings-form-notice">{t(adminNotice)}</p>}
          <button
            type="button"
            className="add-admin-button"
            onClick={handleAddAdmin}
            disabled={Boolean(adminBusy)}
          >
            {adminBusy === 'create' ? t('saving') : t('addAdmin')}
          </button>
        </div>

        {adminAccounts.length > 0 ? (
          <div className="admin-list">
            <h4 className="admin-list-title">{t('adminList')} ({adminAccounts.length})</h4>
            <AdminListToolbar
              searchValue={adminSearch}
              onSearchChange={setAdminSearch}
              searchPlaceholder={t('searchPlaceholder')}
              shownCount={adminAccounts.filter((account) => `${account.email} ${account.uid}`.toLowerCase().includes(adminSearch.trim().toLowerCase())).length}
              totalCount={adminAccounts.length}
              t={t}
              showClear={Boolean(adminSearch.trim())}
              onClear={() => setAdminSearch('')}
            />
            {adminAccounts
              .filter((account) => `${account.email} ${account.uid}`.toLowerCase().includes(adminSearch.trim().toLowerCase()))
              .map((account) => {
                const isSelf = account.uid === auth.currentUser?.uid;
                return (
                  <div key={account.uid} className="admin-item">
                    <div className="admin-item-content">
                      <span className="admin-uuid">{account.email || account.uid}</span>
                      {isSelf && <span className="language-file-badge is-ready">{t('currentAdmin')}</span>}
                    </div>
                    <button
                      className="delete-admin-button"
                      onClick={() => handleRemoveAdmin(account)}
                      disabled={isSelf || Boolean(adminBusy)}
                      title={isSelf ? t('cannotRemoveSelf') : t('removeAdmin')}
                    >
                      {t('removeAdmin')}
                    </button>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="empty-admin-message">
            <p>{t('noAdminsAdded')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users, competition, onFreezeUser, onDeleteUser, onResetRound, t }) => {
  const [userSearch, setUserSearch] = useState('');
  const [frozenFilter, setFrozenFilter] = useState('all');
  const [roundStateFilter, setRoundStateFilter] = useState('all');

  const getRoundState = useCallback((user, roundIndex) => {
    if (!competition?.rounds?.[roundIndex]) return null;
    const round = competition.rounds[roundIndex];
    const roundData = user.roundData?.[roundIndex] || {};
    if (!round.isActive) return null;
    if (roundData.isDone) return 'submitted';
    if (roundData.startTime) return 'running';
    return 'not_started';
  }, [competition]);

  const activeRoundIndex = competition?.rounds?.findIndex((round) => round.isActive) ?? -1;

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    return users.filter((user) => {
      if (frozenFilter === 'frozen' && !user.frozen) return false;
      if (frozenFilter === 'notFrozen' && user.frozen) return false;
      if (roundStateFilter !== 'all' && activeRoundIndex >= 0) {
        if (getRoundState(user, activeRoundIndex) !== roundStateFilter) return false;
      }
      if (!query) return true;
      const haystack = [
        user.name,
        user.id,
        user.dateOfBirth,
        user.gender,
        String(user.score ?? '')
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [users, userSearch, frozenFilter, roundStateFilter, activeRoundIndex, getRoundState]);

  const userFiltersActive = Boolean(userSearch.trim()) || frozenFilter !== 'all' || roundStateFilter !== 'all';
  const userFilters = [
    {
      id: 'frozen',
      label: t('filterStatus'),
      value: frozenFilter,
      onChange: setFrozenFilter,
      options: [
        { value: 'all', label: t('all') },
        { value: 'frozen', label: t('frozen') },
        { value: 'notFrozen', label: t('notFrozen') }
      ]
    }
  ];
  if (activeRoundIndex >= 0) {
    userFilters.push({
      id: 'roundState',
      label: t('filterRoundState'),
      value: roundStateFilter,
      onChange: setRoundStateFilter,
      options: [
        { value: 'all', label: t('all') },
        { value: 'running', label: t('stateRunning') },
        { value: 'submitted', label: t('stateSubmitted') },
        { value: 'not_started', label: t('stateDidNotStart') }
      ]
    });
  }

  return (
    <div className="users-tab">
      <div className="admin-section-header">
        <div>
          <h2>{t('userList')}</h2>
          <p className="admin-section-subtitle">{competition?.name}</p>
        </div>
      </div>
      <AdminListToolbar
        searchValue={userSearch}
        onSearchChange={setUserSearch}
        searchPlaceholder={t('searchUsersPlaceholder')}
        shownCount={filteredUsers.length}
        totalCount={users.length}
        t={t}
        filters={userFilters}
        showClear={userFiltersActive}
        onClear={() => {
          setUserSearch('');
          setFrozenFilter('all');
          setRoundStateFilter('all');
        }}
      />
      <div className="users-table-container">
        <table className="users-table">
        <thead>
          <tr>
            <th>{t('uuid')}</th>
            <th>{t('name')}</th>
            <th>{t('dateOfBirth')}</th>
            <th>{t('gender')}</th>
            <th>{t('currentScore')}</th>
            <th>{t('frozen')}</th>
            {competition?.rounds?.map((round, index) => (
              <th key={index}>
                {t('round')} {index + 1}
                <div className="users-table-subhead">
                  {t('state')}
                </div>
              </th>
            ))}
            <th>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length === 0 ? (
            <tr>
              <td colSpan={6 + (competition?.rounds?.length || 0) + 1} className="admin-table-empty">
                {users.length === 0 ? t('noUsersYet') : t('noMatchingResults')}
              </td>
            </tr>
          ) : filteredUsers.map(user => (
            <tr key={user.id} className={user.frozen ? 'user-row-frozen' : ''}>
              <td className="user-uuid-cell" title={user.id}>
                <code className="user-uuid">{user.id}</code>
              </td>
              <td className="user-name-cell">{user.name}</td>
              <td>{user.dateOfBirth}</td>
              <td>{user.gender ? t(user.gender) : '-'}</td>
              <td><span className="score-pill">{user.score || 0}</span></td>
              <td>
                <span className={`status-badge ${user.frozen ? 'status-frozen' : 'status-ok'}`}>
                  {user.frozen ? t('frozen') : t('notFrozen')}
                </span>
              </td>
              {competition?.rounds?.map((round, index) => {
                const state = getRoundState(user, index);
                const roundData = user.roundData?.[index] || {};
                const roundScore = roundData.score || 0;
                const correctAnswers = roundData.correctAnswers || 0;
                const stateLabel = state === 'submitted' ? t('stateSubmitted')
                  : state === 'running' ? t('stateRunning')
                  : state === 'not_started' ? t('stateDidNotStart')
                  : '-';
                return (
                  <td key={index} className="user-round-cell">
                    <div className={`user-round-state state-${state || 'idle'}`}>
                      {stateLabel}
                    </div>
                    <div className="user-round-meta">
                      {t('score')}: {roundScore} | {t('correct')}: {correctAnswers}
                    </div>
                    <button
                      onClick={() => onResetRound(user.id, index)}
                      className="reset-round-button"
                    >
                      {t('reset')}
                    </button>
                  </td>
                );
              })}
              <td>
                <div className="user-actions">
                  <button
                    onClick={() => onFreezeUser(user.id, !user.frozen)}
                    className={user.frozen ? 'unfreeze-button' : 'freeze-button'}
                  >
                    {user.frozen ? t('unfreeze') : t('freeze')}
                  </button>
                  <button
                    onClick={() => onDeleteUser(user.id, user.name)}
                    className="user-delete-button"
                  >
                    {t('delete')}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

// Challenges Tab Component
const ChallengesTab = ({ competition, challenges, availableLanguages, onAddChallenge, onUpdateChallenge, onDeleteChallenge, onUpdateCompetition, t }) => {
  const { currentLanguage } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [editingChallengeId, setEditingChallengeId] = useState(null);
  
  // Initialize form data with multilingual structure
  const initializeFormData = () => {
    const challengeTextByLang = {};
    const choicesByLang = [{}];
    availableLanguages.forEach(lang => {
      challengeTextByLang[lang.shortcut] = '';
      choicesByLang[0][lang.shortcut] = '';
    });
    choicesByLang[0].image = ''; // Image URL for choice
    
    return {
      text: challengeTextByLang,
      challengeImage: '', // Image URL for challenge
      type: 'single-choice',
      choices: choicesByLang,
      correctAnswers: [],
      category: ''
    };
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [uploadingChallengeImage, setUploadingChallengeImage] = useState(false);
  const [uploadingChoiceImages, setUploadingChoiceImages] = useState({});
  const [challengeImageProgress, setChallengeImageProgress] = useState(0);
  const [choiceImageProgress, setChoiceImageProgress] = useState({});
  const [challengeSearch, setChallengeSearch] = useState('');
  const [challengeCategoryFilter, setChallengeCategoryFilter] = useState('all');
  const [challengeTypeFilter, setChallengeTypeFilter] = useState('all');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
      // Validate that at least one language has challenge text
      const hasChallengeText = availableLanguages.some(lang => 
        formData.text[lang.shortcut]?.trim()
      );
      if (!hasChallengeText) {
        alert(t('challengeTextRequired'));
        return;
      }

    // Validate choices - at least 2 choices with content in at least one language
    const validChoices = formData.choices.filter(choice => 
      availableLanguages.some(lang => choice[lang.shortcut]?.trim())
    );
    if (validChoices.length < 2) {
      alert(t('choicesRequired'));
      return;
    }

    // Validate correct answers based on challenge type
    const isSingleChoice = formData.type === 'single-choice' || formData.type === 'text-image-single-choice';
    if (formData.correctAnswers.length === 0) {
      alert(t('correctAnswerRequired'));
      return;
    }
    if (isSingleChoice && formData.correctAnswers.length > 1) {
      alert(t('singleChoiceOnlyOneAnswer'));
      return;
    }

    if (!formData.category) {
      alert(t('categoryFieldRequired'));
      return;
    }

    if (editingChallengeId) {
      // Update existing challenge
      await onUpdateChallenge(editingChallengeId, {
        ...formData,
        choices: validChoices
      });
      setEditingChallengeId(null);
    } else {
      // Create new challenge
      await onAddChallenge({
        ...formData,
        choices: validChoices,
        number: nextChallengeNumber(challenges, formData.category),
        createdAt: new Date()
      });
    }

    setFormData(initializeFormData());
    setShowForm(false);
  };

  const handleEdit = (challenge) => {
    // Pre-fill form with challenge data
    const challengeTextByLang = {};
    const challengeText = challenge.text || challenge.question; // Support both old and new property names
    availableLanguages.forEach(lang => {
      if (typeof challengeText === 'object') {
        challengeTextByLang[lang.shortcut] = challengeText[lang.shortcut] || '';
      } else {
        // Legacy: if challenge text is a string, put it in the first available language
        challengeTextByLang[lang.shortcut] = availableLanguages[0]?.shortcut === lang.shortcut ? (challengeText || '') : '';
      }
    });

    const choicesByLang = challenge.choices?.map(choice => {
      const choiceObj = {};
      availableLanguages.forEach(lang => {
        if (typeof choice === 'object') {
          choiceObj[lang.shortcut] = choice[lang.shortcut] || '';
        } else {
          // Legacy: if choice is a string, put it in the first available language
          choiceObj[lang.shortcut] = availableLanguages[0]?.shortcut === lang.shortcut ? (choice || '') : '';
        }
      });
      // Add image if it exists
      if (choice && typeof choice === 'object' && choice.image) {
        choiceObj.image = choice.image;
      } else {
        choiceObj.image = '';
      }
      return choiceObj;
    }) || [{}];

    // Handle correctAnswers - it should be an array of indices
    const correctAnswers = Array.isArray(challenge.correctAnswers) 
      ? challenge.correctAnswers.filter(idx => idx !== undefined && idx !== null)
      : [];

    setFormData({
      text: challengeTextByLang,
      challengeImage: challenge.challengeImage || challenge.questionImage || '',
      type: challenge.type || 'single-choice',
      choices: choicesByLang.length > 0 ? choicesByLang : [{}],
      correctAnswers: correctAnswers,
      category: challenge.category || ''
    });

    setEditingChallengeId(challenge.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData(initializeFormData());
    setEditingChallengeId(null);
    setShowForm(false);
  };

  const handleDelete = async (challenge) => {
    const challengeText = typeof (challenge.text || challenge.question) === 'object'
      ? ((challenge.text || challenge.question)[currentLanguage] ||
         Object.values(challenge.text || challenge.question).find(q => q?.trim()) ||
         t('noChallengeAvailable'))
      : (challenge.text || challenge.question);
    const preview = String(challengeText || '').slice(0, 80);
    const confirmMsg = t('deleteChallengeConfirm').replace('{challenge}', preview);
    if (!window.confirm(confirmMsg)) return;
    await onDeleteChallenge(challenge.id);
  };

  const addChoice = () => {
    const newChoice = {};
    availableLanguages.forEach(lang => {
      newChoice[lang.shortcut] = '';
    });
    newChoice.image = ''; // Image URL for choice
    setFormData({
      ...formData,
      choices: [...formData.choices, newChoice]
    });
  };

  const updateChallengeText = (lang, value) => {
    setFormData({
      ...formData,
      text: {
        ...formData.text,
        [lang]: value
      }
    });
  };

  const updateChoice = (choiceIndex, lang, value) => {
    const choices = [...formData.choices];
    choices[choiceIndex] = {
      ...choices[choiceIndex],
      [lang]: value
    };
    setFormData({ ...formData, choices });
  };

  const updateChoiceImage = (choiceIndex, imageUrl) => {
    const choices = [...formData.choices];
    choices[choiceIndex] = {
      ...choices[choiceIndex],
      image: imageUrl
    };
    setFormData({ ...formData, choices });
  };

  const uploadImage = async (file, path, progressKey = null) => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (path.includes('challenges') || path.includes('questions')) {
            setChallengeImageProgress(progress);
          } else if (progressKey) {
            setChoiceImageProgress(prev => ({ ...prev, [progressKey]: progress }));
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleChallengeImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('invalidImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('imageFileTooLarge'));
      return;
    }

    setUploadingChallengeImage(true);
    setChallengeImageProgress(0);

    try {
      const timestamp = Date.now();
      const fileName = `challenges/${competition.id}/${timestamp}_${file.name}`;
      const downloadURL = await uploadImage(file, fileName);
      setFormData({ ...formData, challengeImage: downloadURL });
    } catch (error) {
      console.error('Error uploading challenge image:', error);
      alert(t('errorUploadingImage'));
    } finally {
      setUploadingChallengeImage(false);
      setChallengeImageProgress(0);
    }
  };

  const handleChoiceImageUpload = async (choiceIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('invalidImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('imageFileTooLarge'));
      return;
    }

    const progressKey = `choice_${choiceIndex}`;
    setUploadingChoiceImages(prev => ({ ...prev, [choiceIndex]: true }));
    setChoiceImageProgress(prev => ({ ...prev, [progressKey]: 0 }));

    try {
      const timestamp = Date.now();
      const fileName = `choices/${competition.id}/${choiceIndex}_${timestamp}_${file.name}`;
      const downloadURL = await uploadImage(file, fileName, progressKey);
      updateChoiceImage(choiceIndex, downloadURL);
    } catch (error) {
      console.error('Error uploading choice image:', error);
      alert(t('errorUploadingImage'));
    } finally {
      setUploadingChoiceImages(prev => ({ ...prev, [choiceIndex]: false }));
      setChoiceImageProgress(prev => ({ ...prev, [progressKey]: 0 }));
    }
  };

  const toggleCorrectAnswer = (index) => {
    const isSingleChoice = formData.type === 'single-choice' || formData.type === 'text-image-single-choice';
    
    if (isSingleChoice) {
      // For single-choice, only allow one correct answer
      if (formData.correctAnswers.includes(index)) {
        // If clicking the already selected answer, deselect it
        setFormData({ ...formData, correctAnswers: [] });
      } else {
        // Select only this answer
        setFormData({ ...formData, correctAnswers: [index] });
      }
    } else {
      // For multiple-choice, allow multiple answers
      const correctAnswers = formData.correctAnswers.includes(index)
        ? formData.correctAnswers.filter(i => i !== index)
        : [...formData.correctAnswers, index];
      setFormData({ ...formData, correctAnswers });
    }
  };

  const removeChoice = (index) => {
    const choices = formData.choices.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      choices,
      correctAnswers: formData.correctAnswers
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i)
    });
  };

  const filteredChallenges = useMemo(() => {
    const query = challengeSearch.trim().toLowerCase();
    return challenges.filter((challenge) => {
      if (challengeCategoryFilter !== 'all' && challenge.category !== challengeCategoryFilter) return false;
      if (challengeTypeFilter !== 'all' && challenge.type !== challengeTypeFilter) return false;
      if (!query) return true;
      return getChallengeSearchText(challenge).includes(query);
    });
  }, [challenges, challengeSearch, challengeCategoryFilter, challengeTypeFilter]);

  const challengeFiltersActive = Boolean(challengeSearch.trim()) || challengeCategoryFilter !== 'all' || challengeTypeFilter !== 'all';
  const challengeTypeLabel = (type) => (
    type === 'single-choice' ? t('textSingleChoice') :
    type === 'multiple-choice' ? t('textMultipleChoice') :
    type === 'text-image-single-choice' ? t('textImageSingleChoice') :
    type === 'text-image-multiple-choice' ? t('textImageMultipleChoice') :
    type
  );

  return (
    <div className="challenges-tab">
      <div className="challenges-tab-header">
        <div>
          <h2>{t('challenge')}</h2>
          <p className="admin-section-subtitle">{competition?.name}</p>
        </div>
        <button 
          className="add-challenge-button"
          onClick={() => {
            if (showForm) {
              handleCancel();
            } else {
              setShowForm(true);
            }
          }}
        >
          {showForm ? t('cancel') : t('addChallenge')}
        </button>
      </div>

      {showForm && (
        <AdminModal
          wide
          title={editingChallengeId ? t('edit') : t('addChallenge')}
          onClose={handleCancel}
        >
        <form onSubmit={handleSubmit} className="challenge-form">
          <div className="form-group">
            <label>{t('challengeText')} ({t('inAllLanguages')})</label>
            {availableLanguages.map(lang => (
              <div key={lang.shortcut} className="language-input-group">
                <label className="language-label">
                  {lang.shortcut.toUpperCase()} - {lang.name}:
                </label>
                <textarea
                  value={formData.text[lang.shortcut] || ''}
                  onChange={(e) => updateChallengeText(lang.shortcut, e.target.value)}
                  placeholder={`${t('challengeText')} (${lang.name})`}
                  rows={3}
                />
              </div>
            ))}
            {(formData.type === 'text-image-single-choice' || formData.type === 'text-image-multiple-choice') && (
              <div className="form-input-group" style={{ marginTop: '1rem' }}>
                <label>{t('challengeImage')} ({t('optional')})</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleChallengeImageUpload}
                  disabled={uploadingChallengeImage}
                  style={{ marginBottom: '0.5rem' }}
                />
                {uploadingChallengeImage && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      backgroundColor: '#e0e0e0', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${challengeImageProgress}%`,
                        height: '100%',
                        backgroundColor: 'var(--brand-primary)',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    <small style={{ color: '#666' }}>{Math.round(challengeImageProgress)}% {t('uploading')}</small>
                  </div>
                )}
                {formData.challengeImage && !uploadingChallengeImage && (
                  <div style={{ marginTop: '0.5rem', position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={formData.challengeImage} 
                      alt="Question preview" 
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, challengeImage: '' })}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'rgba(255, 0, 0, 0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title={t('remove')}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>{t('challengeTypes')}</label>
            <select
              value={formData.type}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  type: e.target.value,
                  correctAnswers: []
                });
              }}
            >
              <option value="single-choice">{t('textSingleChoice')}</option>
              <option value="multiple-choice">{t('textMultipleChoice')}</option>
              <option value="text-image-single-choice">{t('textImageSingleChoice')}</option>
              <option value="text-image-multiple-choice">{t('textImageMultipleChoice')}</option>
            </select>
          </div>

          <div className="form-group">
            <label>{t('selectCategory')}</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              <option value="">Select...</option>
              {competition.categories?.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('choices')} ({t('inAllLanguages')})</label>
            {formData.choices.map((choice, index) => (
              <div key={index} className="choice-multilang-group">
                <div className="choice-header">
                  <strong>{t('choice')} {index + 1}</strong>
                  <div className="choice-actions">
                    <button
                      type="button"
                      className={`mark-correct-button ${formData.correctAnswers.includes(index) ? 'correct' : ''}`}
                      onClick={() => toggleCorrectAnswer(index)}
                    >
                      {formData.correctAnswers.includes(index) ? '✓ ' + t('correct') : t('markCorrect')}
                    </button>
                    {formData.choices.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeChoice(index)}
                        className="remove-choice-button"
                      >
                        {t('remove')}
                      </button>
                    )}
                  </div>
                </div>
                {availableLanguages.map(lang => (
                  <div key={lang.shortcut} className="choice-language-input">
                    <label className="language-label-small">
                      {lang.shortcut.toUpperCase()}:
                    </label>
                    <input
                      type="text"
                      value={choice[lang.shortcut] || ''}
                      onChange={(e) => updateChoice(index, lang.shortcut, e.target.value)}
                      placeholder={`${t('choice')} ${index + 1} (${lang.name})`}
                    />
                  </div>
                ))}
                {(formData.type === 'text-image-single-choice' || formData.type === 'text-image-multiple-choice') && (
                  <div className="form-input-group" style={{ marginTop: '0.5rem' }}>
                    <label>{t('choiceImage')} ({t('optional')})</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleChoiceImageUpload(index, e)}
                      disabled={uploadingChoiceImages[index]}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    {uploadingChoiceImages[index] && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ 
                          width: '100%', 
                          height: '8px', 
                          backgroundColor: '#e0e0e0', 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${choiceImageProgress[`choice_${index}`] || 0}%`,
                            height: '100%',
                            backgroundColor: 'var(--brand-primary)',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <small style={{ color: '#666' }}>{Math.round(choiceImageProgress[`choice_${index}`] || 0)}% {t('uploading')}</small>
                      </div>
                    )}
                    {choice.image && !uploadingChoiceImages[index] && (
                      <div style={{ marginTop: '0.5rem', position: 'relative', display: 'inline-block' }}>
                        <img 
                          src={choice.image} 
                          alt={`Choice ${index + 1} preview`}
                          style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => updateChoiceImage(index, '')}
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            background: 'rgba(255, 0, 0, 0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          title={t('remove')}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={addChoice} className="add-choice-button">
              {t('addChoice')}
            </button>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-challenge-button">
              {editingChallengeId ? t('update') : t('save')}
            </button>
            <button type="button" onClick={handleCancel}>
              {t('cancel')}
            </button>
          </div>
        </form>
        </AdminModal>
      )}

      <div className="challenges-list">
        <h3>{t('allChallenges')} ({challenges.length})</h3>
        <AdminListToolbar
          searchValue={challengeSearch}
          onSearchChange={setChallengeSearch}
          searchPlaceholder={t('searchChallengesPlaceholder')}
          shownCount={filteredChallenges.length}
          totalCount={challenges.length}
          t={t}
          showClear={challengeFiltersActive}
          onClear={() => {
            setChallengeSearch('');
            setChallengeCategoryFilter('all');
            setChallengeTypeFilter('all');
          }}
          filters={[
            {
              id: 'category',
              label: t('filterCategory'),
              value: challengeCategoryFilter,
              onChange: setChallengeCategoryFilter,
              options: [
                { value: 'all', label: t('all') },
                ...(competition.categories || []).map((cat) => ({ value: cat, label: cat }))
              ]
            },
            {
              id: 'type',
              label: t('filterType'),
              value: challengeTypeFilter,
              onChange: setChallengeTypeFilter,
              options: [
                { value: 'all', label: t('all') },
                { value: 'single-choice', label: t('textSingleChoice') },
                { value: 'multiple-choice', label: t('textMultipleChoice') },
                { value: 'text-image-single-choice', label: t('textImageSingleChoice') },
                { value: 'text-image-multiple-choice', label: t('textImageMultipleChoice') }
              ]
            }
          ]}
        />
        {filteredChallenges.length === 0 ? (
          <div className="admin-empty-state">
            <p>{challenges.length === 0 ? t('noChallengesYet') : t('noMatchingResults')}</p>
          </div>
        ) : (
          filteredChallenges.map(challenge => {
          // Extract challenge text in current language or fallback
          const challengeText = typeof (challenge.text || challenge.question) === 'object'
            ? ((challenge.text || challenge.question)[currentLanguage] || 
               Object.values(challenge.text || challenge.question).find(q => q?.trim()) || 
               t('noChallengeAvailable'))
            : (challenge.text || challenge.question);
          
          return (
            <div key={challenge.id} className="challenge-item">
              <div className="challenge-content">
                {challenge.number != null && (
                  <span className="challenge-number-badge">#{challenge.number}</span>
                )}
                <p className="challenge-text-preview"><strong>{t('challengeText')}:</strong> {challengeText}</p>
                <div className="challenge-meta-row">
                  <span className="status-badge status-category">{challenge.category}</span>
                  <span className="status-badge status-type">{challengeTypeLabel(challenge.type)}</span>
                </div>
                <p className="challenge-id-line"><strong>{t('challengeUuid')}:</strong> <code>{challenge.id}</code></p>
                <p><strong>{t('challengeCreatedAt')}:</strong> {formatTimestamp(challenge.createdAt) || '-'}</p>
              </div>
              <div className="challenge-actions">
                <button
                  onClick={() => handleEdit(challenge)}
                  className="edit-button"
                >
                  {t('edit')}
                </button>
                <button
                  onClick={() => handleDelete(challenge)}
                  className="challenge-delete-button"
                >
                  {t('delete')}
                </button>
              </div>
            </div>
          );
        })
        )}
      </div>
    </div>
  );
};

// Rounds Tab Component
const RoundsTab = ({ competition, onStartRound, onStopRound, onUpdateRound, t }) => {
  const [roundTimes, setRoundTimes] = useState({});
  const [savingRoundIndex, setSavingRoundIndex] = useState(null);

  useEffect(() => {
    const next = {};
    competition.rounds?.forEach((round, i) => {
      next[i] = { start: toDateTimeLocal(round?.startTime), end: toDateTimeLocal(round?.endTime) };
    });
    setRoundTimes(next);
  }, [competition?.rounds]);

  const handleSaveRoundTimes = async (roundIndex) => {
    setSavingRoundIndex(roundIndex);
    try {
      const rt = roundTimes[roundIndex] || {};
      await onUpdateRound(roundIndex, {
        startTime: rt.start ? Timestamp.fromDate(new Date(rt.start)) : null,
        endTime: rt.end ? Timestamp.fromDate(new Date(rt.end)) : null
      });
    } finally {
      setSavingRoundIndex(null);
    }
  };

  const setRoundTime = (roundIndex, field, value) => {
    setRoundTimes(prev => ({
      ...prev,
      [roundIndex]: { ...(prev[roundIndex] || {}), [field]: value }
    }));
  };

  // Check if any round is currently running
  const hasActiveRound = competition.rounds?.some(round => round.isActive === true) || false;
  
  const canStartRound = (roundIndex) => {
    // If any round is currently active, no round can be started
    if (hasActiveRound) {
      return false;
    }
    
    // If no rounds are active, check sequential order
    if (roundIndex === 0) {
      // First round can be started if it's not already active
      const currentRound = competition.rounds[roundIndex];
      return !currentRound || !currentRound.isActive;
    }
    
    // Check if previous round has been started
    const previousRound = competition.rounds[roundIndex - 1];
    if (!previousRound) {
      return false;
    }
    
    // Round can be started if previous round has been started (hasStarted flag)
    return previousRound.hasStarted === true;
  };

  return (
    <div className="rounds-tab">
      <div className="admin-section-header">
        <div>
          <h2>{t('rounds')}</h2>
          <p className="admin-section-subtitle">{competition?.name}</p>
        </div>
      </div>
      {competition.rounds?.map((round, index) => {
        const canStart = canStartRound(index);
        const isDisabled = !round.isActive && !canStart;
        
        return (
          <div key={index} className={`round-item ${round.isActive ? 'is-active' : ''}`}>
            <div className="round-item-header">
              <h3>
                {t('round')} {index + 1}
              </h3>
              <span className={`status-badge ${round.isActive ? 'status-active' : 'status-inactive'}`}>
                {round.isActive ? t('active') : t('inactive')}
              </span>
            </div>
            <div className="round-info">
              {(round.startTime || round.endTime) && (
                <p><strong>{t('roundPeriod')}:</strong> {formatTimestamp(round.startTime) || t('manualControl')} – {formatTimestamp(round.endTime) || t('manualControl')}</p>
              )}
              <div className="round-period-edit">
                <p className="period-hint">{t('setRoundPeriodHint')}</p>
                <div className="period-fields">
                  <div className="form-group">
                    <label>{t('startTimeLabel')}</label>
                    <input
                      type="datetime-local"
                      value={roundTimes[index]?.start ?? ''}
                      onChange={(e) => setRoundTime(index, 'start', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('endTimeLabel')}</label>
                    <input
                      type="datetime-local"
                      value={roundTimes[index]?.end ?? ''}
                      onChange={(e) => setRoundTime(index, 'end', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="save-times-button"
                    onClick={() => handleSaveRoundTimes(index)}
                    disabled={savingRoundIndex === index}
                  >
                    {savingRoundIndex === index ? t('loading') : t('saveTimes')}
                  </button>
                </div>
              </div>
              <div className="round-meta-grid">
                <div><span>{t('maxTimePerRound')}</span><strong>{round.maxTime}s</strong></div>
                <div><span>{t('roundType')}</span><strong>{round.type === 'fixed' ? t('fixedChallenges') : t('openChallenges')}</strong></div>
                <div><span>{t('numberOfChallenges')}</span><strong>{round.type === 'fixed' ? (round.numberOfChallenges || 0) : t('all')}</strong></div>
                <div><span>{t('questionOrder')}</span><strong>{round.shuffleQuestions === false ? t('orderUploadOrder') : t('orderRandomPerUser')}</strong></div>
                <div><span>{t('choiceOrder')}</span><strong>{round.shuffleChoices === false ? t('orderUploadOrder') : t('orderRandomPerUser')}</strong></div>
                <div><span>{t('selectCategories')}</span><strong>{round.categories?.join(', ') || t('none')}</strong></div>
                <div><span>{t('correctSolutionValue')}</span><strong>+{round.correctValue}</strong></div>
                <div><span>{t('wrongSolutionValue')}</span><strong>{round.wrongValue}</strong></div>
              </div>
            </div>
            <div className="round-actions">
              {!round.isActive ? (
                <button
                  className="start-button"
                  onClick={() => onStartRound(index)}
                  disabled={isDisabled}
                  title={isDisabled ? (t('mustStartPreviousRound') || `You must start Round ${index} first.`) : ''}
                >
                  {t('startRound')}
                </button>
              ) : (
                <button
                  className="stop-button"
                  onClick={() => onStopRound(index)}
                >
                  {t('stopRound')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminPage;

